import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_TYPES,
  NOTIFICATION_TYPES,
} from '../../../shared/constants';
import { IContactForm, IFormValues } from '../../../types/shared';

import FormMessage, { FORM_TYPES } from '../../atoms/form-message/FormMessage';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

import { DEQ_COMPS } from '../../../utils/deq';
import { FORM_STATES } from '../../../utils/forms/constants';
import { renderFormInput } from '../../../utils/forms/renderFormInput';
import { ReactMarkdownExtended } from '../../../utils/generic';
import { LABELS } from '../../../utils/language/form-labels';
import languageHelper from '../../../utils/language/language-helper';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { contactFormData } from '../../../data/static-content/forms/contact-form/static-content';
import {
  createGraphMailTemplate,
  handleReCaptcha,
} from '../../../services/re-captcha.service';
import { useWizardStore } from '../../../state/wizard/Wizard.store';
import { WizardItem } from '../../../types/wizard';
import FormStatusCard from '../../molecules/form-status-card/FormStatusCard';
import Form from '../../organisms/form/Form';

import { getActiveItems } from './WizardContactForm.helpers';
import {
  parseActiveItemsToText,
  parseGroupedResults,
  parseResultsToText,
} from './WizardResultLayout.helpers';
import { WizardItemResult, WizardResult } from './WizardResultLayout.types';

export interface WizardContactFormProps {
  form: IContactForm;
  slug: string;
  customEmail?: string;
  onError: () => void;
  onPreviousStep: () => void;
  onFormSubmit: (result: IFormValues) => void;
}

const WizardContactForm = ({
  form,
  slug,
  customEmail,
  onError,
  onPreviousStep,
  onFormSubmit,
}: WizardContactFormProps) => {
  const { results, steps, getItem } = useWizardStore();
  const [formState, setFormState] = useState(FORM_STATES.default);
  const [isNotificationOpen, setIsNotificationOpen] = useState(true);

  const { lang } = languageHelper();
  const { email } = form;

  const onPreviousButtonClick = () => {
    onPreviousStep();
  };

  const callback = (response: { message: string }) => {
    if (response.message === 'Sent e-mail') {
      setFormState(FORM_STATES.success);
    }

    return response;
  };

  const handleSendError = () => {
    setFormState(FORM_STATES.error);
    onError();
  };

  const handleSubmit = async (formResult: IFormValues) => {
    const emailAddresses = email?.emailAddresses;

    if (customEmail) {
      emailAddresses.push(customEmail);
    }

    setFormState(FORM_STATES.loading);

    const activeItems = getActiveItems(steps);
    const groupedResults = parseGroupedResults(results);
    const resultsListTotal = groupedResults.map((result): WizardItemResult => {
      const groupItem = getItem(result.id);

      return {
        title: groupItem.title || '',
        results: result.items.reduce(
          (accumulator: WizardResult[], item: WizardItem): WizardResult[] => {
            const foundResults = item.textValue
              ? [
                  {
                    title: `${item.title}: ${item.textValue}`,
                    role: item.visual?.tab?.value,
                  },
                ]
              : item.visual?.results?.map((visualResult) => ({
                  title: visualResult.title,
                  role: item.visual?.tab?.value,
                }));

            return [...accumulator, ...(foundResults || [])] as WizardResult[];
          },
          []
        ),
      };
    });

    const resultsListAdvisor = resultsListTotal.filter(
      (result) =>
        !!result.results.filter((item) => item.role === 'advisor').length
    );
    const resultsListPersonal = resultsListTotal.filter(
      (result) => !!result.results.filter((item) => item.role === 'self').length
    );

    const parsedResultListAdvisor = parseResultsToText(resultsListAdvisor);
    const parsedResultListPersonal = parseResultsToText(resultsListPersonal);
    const parsedActiveItems = parseActiveItemsToText(activeItems);

    const emailTemplate = createGraphMailTemplate(
      {
        ...formResult,
        primaryResults: parsedResultListAdvisor,
        secondaryResults: parsedResultListPersonal,
        activeItems: parsedActiveItems,
      },
      emailAddresses,
      email.title,
      email.body
    );

    handleReCaptcha(emailTemplate, callback, handleSendError);

    onFormSubmit(formResult);
  };

  return (
    <div>
      {formState === FORM_STATES.error && (
        <FormStatusCard
          title={contactFormData.userFeedback.error.title[lang]}
          statusType={NOTIFICATION_TYPES.error}
          show={formState === FORM_STATES.error}
          primaryButtonLabel={
            contactFormData.userFeedback.error.primaryButtonText[lang]
          }
          customPrimaryClick={() => setFormState(FORM_STATES.default)}
        >
          <ReactMarkdownExtended componentType={DEQ_COMPS.wizardContactForm}>
            {contactFormData.userFeedback.error.description[lang]}
          </ReactMarkdownExtended>
        </FormStatusCard>
      )}
      {formState === FORM_STATES.loading && (
        <FormStatusCard
          statusType={NOTIFICATION_TYPES.loading}
          show={formState === FORM_STATES.loading}
        >
          <ReactMarkdownExtended componentType={DEQ_COMPS.wizardContactForm}>
            {contactFormData.userFeedback.loading.email[lang]}
          </ReactMarkdownExtended>
        </FormStatusCard>
      )}
      {formState === FORM_STATES.default && (
        <>
          {form.rawSubtitle?.childMarkdownRemark && (
            <BodyIntro>
              <ReactMarkdownExtended
                componentType={DEQ_COMPS.wizardContactForm}
              >
                {form.rawSubtitle.childMarkdownRemark.rawMarkdownBody}
              </ReactMarkdownExtended>
            </BodyIntro>
          )}
          {form.description && (
            <BodyNormal data-testid="description">
              <ReactMarkdownExtended
                componentType={DEQ_COMPS.wizardContactForm}
              >
                {form.description.childMarkdownRemark.rawMarkdownBody}
              </ReactMarkdownExtended>
            </BodyNormal>
          )}
          <Form id={form.id} trackPageView={false} submit={handleSubmit}>
            <FormCard>
              <StyledH3>Gegevens</StyledH3>
              {form.showPrivacyStatement && (
                <FormMessage
                  type={FORM_TYPES.info}
                  isNotificationOpen={isNotificationOpen}
                  onNotificationClick={(event) => setIsNotificationOpen(event)}
                >
                  <ReactMarkdownExtended
                    componentType={DEQ_COMPS.wizardContactForm}
                  >
                    {
                      contactFormData.infoMessage.privacyPolicyAndReCaptcha[
                        lang
                      ]
                    }
                  </ReactMarkdownExtended>
                </FormMessage>
              )}
              <InputWrapper>
                {form.formInputs?.map((item, key) =>
                  renderFormInput(item, key)
                )}
              </InputWrapper>
            </FormCard>
            <CtaWrapper>
              <StyledPrimaryButton
                type={BUTTON_TYPES.internal}
                buttonAction={BUTTON_TYPES.submit}
                icon={ICON_TYPES.none}
                buttonTheme={BUTTON_THEMES.muted}
                label="Vorige"
                onButtonClick={(
                  to: string,
                  type: string,
                  event: MouseEvent
                ) => {
                  event.stopPropagation();
                  onPreviousButtonClick();
                }}
              />
              <StyledPrimaryButton
                type={BUTTON_TYPES.submit}
                data-testid="submit-button"
                buttonAction={BUTTON_TYPES.submit}
                label={LABELS.submit[lang]}
                icon={ICON_TYPES.none}
                buttonTheme={BUTTON_THEMES.default}
                deqComponentTitle={LABELS.submit[lang]}
                deqCustomAttributes={{
                  componentType: 'contact-form',
                  elementTarget: `${slug}`,
                  elementAction: BUTTON_TYPES.submit,
                }}
              />
            </CtaWrapper>
          </Form>
        </>
      )}
    </div>
  );
};

export default WizardContactForm;

const formParagraphStyles = css`
  color: ${Colors.abnBlack};
  margin-bottom: ${Spacings.M};

  b {
    font-weight: bold;
  }

  p {
    margin: 0 0 ${Spacings.S};

    &:last-of-type {
      margin: 0;
    }
  }
`;

const BodyIntro = styled(IntroText)`
  ${formParagraphStyles}
  display: block;
  margin-bottom: ${Spacings.S};

  ${mq.to.breakpoint.M`
    margin: 0 ${Spacings.S} ${Spacings.S};
  `}
`;

const BodyNormal = styled(Body)`
  ${formParagraphStyles}

  h3 {
    margin: 0 0 ${Spacings.XS};
  }

  h4 {
    margin-bottom: ${Spacings.XS};
  }
`;

const FormCard = styled.div`
  background-color: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
  padding: ${Spacings.M} ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
  `}
`;

const InputWrapper = styled.div`
  & > * {
    margin-bottom: ${Spacings.M};
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  flex: 1;

  ${mq.from.breakpoint.M`
    flex: none;
  `}
`;

const StyledH3 = styled(H3)`
  margin-top: 0;
`;

const CtaWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${Spacings.S};
  margin: ${Spacings.M} 0 ${Spacings.XS} 0px;
  padding: 0 ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: 0;
    justify-content: space-between;
    flex-direction: row;
  `}

  ${mq.from.breakpoint.L`
    margin: ${Spacings.XL} 0px ${Spacings.S} 0px;
  `}
`;
